import React, { Component, Fragment } from 'react';
import BaseDataTable, { PAGE_SIZE, PAGE_SIZE_LARGE } from '../../../BaseDataTable';
import DaypartTableHeader from './DaypartTableHeader';
import DaypartTableRow from './DaypartTableRow';
import DaypartTableSummary from './DaypartTableSummary';
import ModuleExport from '../../../ModuleExport';
import styles from './styles.scss';
import { orderBy, get } from 'lodash';
import { arrayOf, shape, string, number, func, bool } from 'prop-types';
import {
  MIN_LENGTH_DAYPART_DATA,
  DAYPART_COMPILATION_CHART_ID,
  MAX_NUMBER_TABLE_ROWS_TO_TRUNCATE,
} from '../../../../helpers/constant';
import cx from 'classnames';
import DeleteReportItemButton from '../../../DeleteReportItem';

class DaypartTable extends Component {
  static propTypes = {
    data: arrayOf(shape({
      time: string,
      visit: number,
      adVisit: number,
      nonAdVisit: number,
      user: number,
      newAdUsers: number,
      nonNewAdUsers: number,
      ads: number,
      avgAdCost: number,
      sumAvgAdCost: number
    })),
    initialSortStatus: shape({
      sortKey: string,
      sortType: string
    }),
    fileName: string,
    campaignAvgAdCost: number,
    onCellClick: func,
    daypartDetailSelector: shape({
      type: string,
      timeRange: string
    }),
    inpdf: bool,
    page: number,
    rowsPerPage: number,
    isGrandTotalDisplay: bool,
    isCustomReport: bool,
    showDeleteDaypartTableButton: bool,
    displayDaypartTable: bool,
    currentStep: string,
    displayDaypartChart: bool,
    itemIndex: number,
    isShowNewAdUsers: bool,
    rowsFirstPage:number
  };

  static defaultProps = {
    data: [],
    initialSortStatus: { sortKey: 'visitsPerAd', sortType: 'desc' },
    fileName: 'daypart_data_table',
    daypartDetailSelector: {
      type: 'adVisit',
      timeRange: 'byDayPart'
    }
  };

  generateHeaders() {
    const { campaignAvgAdCost, daypartDetailSelector, isShowNewAdUsers } = this.props;
    const timeRange = daypartDetailSelector.timeRange;
    const headerDateLabels =
    {
      byDayPart: 'Daypart',
      hourly: 'Hour',
      byDayOfWeek: 'Day of Week',
      daily: 'Date',
      weekly: 'Week',
      monthly: 'Month'
    };

    const headers = isShowNewAdUsers ?    
    [
      { title: headerDateLabels[timeRange], key: 'sortTimeKey' },
      { title: 'Total Visits', key: 'visit' },
      { title: 'New Ad Users', key: 'newAdUsers' },
      { title: 'Ads', key: 'ads' },
      { title: 'New Ad Users/Ads', key: 'newAdUsersPerAds' }
    ] :
    [
      { title: headerDateLabels[timeRange], key: 'sortTimeKey' },
      { title: 'Total Visits', key: 'visit' },
      { title: 'Ad Visits', key: 'adVisit' },
      { title: 'Ads', key: 'ads' },
      { title: 'Ad Visits/Ads', key: 'visitsPerAd' }
    ];
    campaignAvgAdCost != null && headers.push({ title: 'Avg. Ad Cost', key: 'avgAdCost' });
    return { headers, campaignAvgAdCost };
  }

  generateTableData() {
    const {
      data,
      campaignAvgAdCost,
      inpdf,
      page,
      rowsPerPage,
      isCustomReport,
      displayDaypartChart,
      initialSortStatus,
      daypartDetailSelector,
      rowsFirstPage
    } = this.props;
    const timeRange = get(daypartDetailSelector, 'timeRange', '');
    const isShowTimeWithYear =  timeRange === 'daily' || timeRange === 'weekly';
    const dataSet = data && data.reduce((acc, entry, index) => {
      const {
        time,
        timeWithYear,
        ads,
        adVisit,
        avgAdCost,
        sumAvgAdCost,
        pricedAds,
        visit,
        newAdUsers
      } = entry;
      const visitsPerAd = (
        ads === 0 ? 0 :
          parseFloat((adVisit / ads).toFixed(2))
      );

      const newAdUsersPerAds = ads === 0 ? 0 : parseFloat((newAdUsers / ads).toFixed(2));

      if (data) {
        acc.tableRowData.push({
          time: isShowTimeWithYear ? timeWithYear : time,
          timeWithYear,
          adVisit,
          ads,
          visitsPerAd,
          visit,
          avgAdCost,
          sortTimeKey: index,
          sumAvgAdCost,
          newAdUsers,
          newAdUsersPerAds
        });
      }
      acc.summaryData.totalAdVisits += adVisit || 0;
      acc.summaryData.totalAds += ads || 0;
      acc.summaryData.totalVisits += visit || 0;
      acc.summaryData.totalNewAdUsers += newAdUsers || 0;
      acc.summaryData.sumAvgAdCost += sumAvgAdCost || 0;
      acc.summaryData.pricedAds += pricedAds || 0;
      return acc;
    }, {
      tableRowData: [],
      summaryData: {
        title: 'Grand Total',
        totalAdVisits: 0,
        totalAds: 0,
        totalVisitsPerAd: 0,
        totalVisits: 0,
        totalNewAdUsers: 0,
        sumAvgAdCost: 0,
        campaignAvgAdCost: 0,
        pricedAds: 0 
      } 
    });
    
    dataSet.summaryData.totalVisitsPerAd =
      !dataSet.summaryData.totalAds ? 0 :
        parseFloat((dataSet.summaryData.totalAdVisits / dataSet.summaryData.totalAds).toFixed(2)) || 0;
    dataSet.summaryData.campaignAvgAdCost =
      campaignAvgAdCost === null ? null :
        parseFloat((dataSet.summaryData.sumAvgAdCost / dataSet.summaryData.pricedAds)) || 0;
    dataSet.summaryData.totalNewAdUsersPerAds =
      !dataSet.summaryData.totalAds ? 0 :
        parseFloat((dataSet.summaryData.totalNewAdUsers / dataSet.summaryData.totalAds).toFixed(2)) || 0;
    
    if (inpdf) {
      const { sortKey, sortType } = initialSortStatus || {};
      dataSet.tableRowData = orderBy(dataSet.tableRowData, [sortKey], sortType);
      const rowOnFirstPage = rowsFirstPage || MIN_LENGTH_DAYPART_DATA;
      const minRows = isCustomReport ? rowOnFirstPage : MIN_LENGTH_DAYPART_DATA;

      const { tableRowsFirstPage, tableRowsOtherPage } = calculateTableRows({ displayChart: displayDaypartChart, isCustomReport, minRows, truncatedRows: 0 });
      if (page === 0) {
        let dataRow = rowsPerPage;
        if(rowsPerPage >= dataSet.tableRowData.length) {
          dataRow = dataSet.tableRowData.length;
        }
        dataSet.tableRowData = dataSet.tableRowData.slice(0, dataRow);
      } else {
        const startIndex = (tableRowsFirstPage + 1) + (page - 1) * (tableRowsOtherPage + 1);
        dataSet.tableRowData = dataSet.tableRowData.slice(startIndex, startIndex + rowsPerPage);
      }
    }
    return dataSet;
  }

  prepCsvData(data, campaignAvgAdCost, initialSortStatus) {
    const { isShowNewAdUsers } = this.props;
    const csvHeaders =
      isShowNewAdUsers ?
      ['Time Range', 'Total Visits', 'New Ad Users', 'Ads', 'New Ad Users/Ads'] :
      ['Time Range', 'Total Visits', 'Ad Visits', 'Ads', 'Ad Visits/Ads'];

    const sortKey = initialSortStatus.sortKey === 'visitsPerAd' ? 'visitsPerAd' : 'time';
    const sortData = orderBy(data, [sortKey], initialSortStatus.sortType);
    campaignAvgAdCost != null && csvHeaders.push('Avg. Ad Cost');
    return sortData && sortData.reduce((acc, entry) => {
      const { time, adVisit, ads, visit, visitsPerAd, avgAdCost, newAdUsers, newAdUsersPerAds } = entry;
      const formattedAvgAdCost = typeof avgAdCost === 'number' ? avgAdCost.toFixed(2) : 'N/A';

      const csvData = 
        isShowNewAdUsers ?
        [time, visit, newAdUsers, ads, newAdUsersPerAds] :
        [time, visit, adVisit, ads, visitsPerAd];
      
      campaignAvgAdCost != null && csvData.push(formattedAvgAdCost);
      acc.push(csvData);
      return acc;
    }, [csvHeaders]);
  }

  handleCellClick = ({ data }) => {
    const { onCellClick } = this.props;
    onCellClick && onCellClick(data);
  }

  exportContainer = React.createRef();

  render() {
    const {
      initialSortStatus,
      fileName, 
      inpdf, 
      page, 
      rowsPerPage, 
      isGrandTotalDisplay,
      isCustomReport,
      displayDaypartTable,
      showDeleteDaypartTableButton,
      currentStep,
      itemIndex,
      isShowNewAdUsers,
      daypartDetailSelector
    } = this.props;
    const dataSet = this.generateTableData();

    const { headers, campaignAvgAdCost } = this.generateHeaders();
    const csvData = this.prepCsvData(dataSet.tableRowData, campaignAvgAdCost, initialSortStatus);
    const timeRange = get(daypartDetailSelector, 'timeRange', '');
    const isDayOfWeek = timeRange === 'byDayOfWeek';

    return (
      <Fragment>
        {!isCustomReport || isCustomReport && displayDaypartTable ?
          <div data-test="daypartTable" className={cx(styles.tableContainer, {[styles.itemContainer]: isCustomReport, [styles.deleteItemContainer]: currentStep === 'customize'})}>
          {
            !inpdf && (
              <ModuleExport
                exportImg={this.exportContainer}
                data={csvData}
                fileName={fileName}
              />
            )
          }
            <div className={styles.exportContainer} ref={this.exportContainer}>
              {showDeleteDaypartTableButton &&
                <DeleteReportItemButton
                  isDelete={showDeleteDaypartTableButton}
                  parentId="daypart"
                  componentId="daypartAnalyticsTable"
                  customClassName={styles.deleteButton}
                  itemIndex={itemIndex}
                />
              }
              <BaseDataTable
                data={dataSet.tableRowData}
                tableHead={<DaypartTableHeader headers={headers} />}
                tableRow={
                  <DaypartTableRow
                    isShowNewAdUsers={isShowNewAdUsers}
                    campaignAvgAdCost={campaignAvgAdCost}
                    isCustomReport={isCustomReport}
                  />
                }
                tableSummary={<DaypartTableSummary isShowNewAdUsers={isShowNewAdUsers} summaryData={dataSet.summaryData} />}
                pagination
                initialSortStatus={initialSortStatus}
                onBodyCellClick={this.handleCellClick}
                inpdf={inpdf}
                page={page}
                rowsPerPage={rowsPerPage}
                isGrandTotalDisplay={isGrandTotalDisplay}
                tableId={DAYPART_COMPILATION_CHART_ID}
                tableIndex={itemIndex}
                isCustomReport={isCustomReport}
                pageSize={isDayOfWeek ? PAGE_SIZE_LARGE : PAGE_SIZE}
              />
            </div>
          </div> : '' }
      </Fragment>
    )
  }
};

import { connect } from 'react-redux';
import { calculateTableRows } from '../../../DashboardTab/Report/helper';

function mapStateToProps(state) {
  return {
    currentStep: get(state, 'reportBuilder.currentStep', ''),
  }
}

export default connect(mapStateToProps)(DaypartTable);
